/**
 * Calculation Sources Report â€“ PDF listing every value and where it came from.
 * Used for transparency and audit of solar design calculations.
 */

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { CalculationResults } from "./solarCalculationEngine";
import type { BOQSummary } from "./boqCalculationService";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export interface CalculationSourcesData {
  projectName: string;
  locationName: string;
  latitude: number;
  longitude: number;
  altitude: number;
  tilt: number;
  azimuth: number;
  mountingType: string;
  surfaceType: string;
  userType: string;
  monthlyConsumption: number[];
  yearlyConsumption: number;
  baseYearlyConsumption?: number;
  morningConsumptionPct?: number;
  effectiveYearlyConsumption?: number;
  customTariffSarPerKwh?: number;
  gridPhase?: number;
  acCableDistanceM?: number;
  dcCableDistanceM?: number;
  design: {
    systemSize: number;
    panelCount: number;
    yearlyProduction: number;
    offset: number;
    initialSystemSize?: number;
    inverterAcCapKw?: number;
    selectedInverterAcKw?: number;
    selectedInverterCount?: number;
    dcAcRatioFinal?: number;
    monthlyResults?: CalculationResults["monthlyResults"];
  };
  calculationResults: CalculationResults | null;
  financials: {
    totalCost: number;
    paybackPeriod: number;
    roi: number;
    savingsYear1: number;
    savings25Year: number;
    monthlyPayment: number;
  };
  boq: BOQSummary | null;
  selectedModule: { manufacturer: string; model: string; power_stc: number } | null;
  selectedInverter: { manufacturer: string; model: string; power_ac_nominal: number } | null;
}

function row(doc: jsPDF, y: number, param: string, value: string, source: string, maxY: number): number {
  const lineH = 5;
  const col1 = 15;
  const col2 = 75;
  const col3 = 130;
  const wrap = 58;
  const sources = doc.splitTextToSize(source, wrap);
  const need = Math.max(1, sources.length) * lineH;
  if (y + need > maxY) {
    doc.addPage();
    y = 20;
  }
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(param, col1, y);
  doc.setFont("helvetica", "normal");
  doc.text(value, col2, y);
  doc.setTextColor(60, 60, 60);
  sources.forEach((line: string, i: number) => {
    doc.text(line, col3, y + i * lineH);
  });
  doc.setTextColor(0, 0, 0);
  return y + need + 2;
}

export type CalculationPdfDisposition = "download" | "blob" | "both";

export interface GenerateCalculationPdfOptions {
  disposition?: CalculationPdfDisposition;
}

export async function generateCalculationSourcesPDF(
  data: CalculationSourcesData,
  options?: GenerateCalculationPdfOptions
): Promise<void | Blob> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageH = 277;
  let y = 20;

  doc.setFontSize(18);
  doc.setTextColor(234, 88, 12);
  doc.text("Calculation Sources", 15, y);
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  y += 8;
  doc.text(`Project: ${data.projectName} | Generated: ${new Date().toLocaleString()}`, 15, y);
  y += 12;

  const res = data.calculationResults;
  const morningConsumptionPct =
    typeof data.morningConsumptionPct === "number" && Number.isFinite(data.morningConsumptionPct)
      ? Math.max(0, Math.min(100, data.morningConsumptionPct))
      : 100;
  const baseYearlyConsumption =
    typeof data.baseYearlyConsumption === "number" && Number.isFinite(data.baseYearlyConsumption)
      ? Math.max(0, data.baseYearlyConsumption)
      : Math.max(0, data.yearlyConsumption);
  const effectiveYearlyConsumption =
    typeof data.effectiveYearlyConsumption === "number" && Number.isFinite(data.effectiveYearlyConsumption)
      ? Math.max(0, data.effectiveYearlyConsumption)
      : Math.max(0, data.yearlyConsumption);
  const customTariffSarPerKwh =
    typeof data.customTariffSarPerKwh === "number" && Number.isFinite(data.customTariffSarPerKwh) && data.customTariffSarPerKwh > 0
      ? data.customTariffSarPerKwh
      : null;
  const derivedTariffSarPerKwh =
    data.design.yearlyProduction > 0
      ? data.financials.savingsYear1 / data.design.yearlyProduction
      : 0;

  // ---------- 1. User inputs ----------
  doc.setFontSize(12);
  doc.setTextColor(234, 88, 12);
  doc.text("1. User inputs (from Location & Energy steps)", 15, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);

  y = row(doc, y, "Location name", data.locationName, "User input (Location step).", pageH);
  y = row(doc, y, "Latitude", `${data.latitude.toFixed(4)}Â°`, "User input (Location / map).", pageH);
  y = row(doc, y, "Longitude", `${data.longitude.toFixed(4)}Â°`, "User input (Location / map).", pageH);
  y = row(doc, y, "Altitude", `${data.altitude} m`, "User input or default 0.", pageH);
  y = row(doc, y, "Tilt", `${data.tilt}Â°`, "User input (Location / mounting).", pageH);
  y = row(doc, y, "Azimuth", `${data.azimuth}Â°`, "User input (180Â° = south).", pageH);
  y = row(doc, y, "Mounting type", data.mountingType, "User input (Location).", pageH);
  y = row(doc, y, "Surface type", data.surfaceType, "User input (Location).", pageH);
  y = row(doc, y, "User type", data.userType, "User input (Residential/Commercial/Industrial/Agricultural).", pageH);
  y = row(doc, y, "Original yearly consumption", `${baseYearlyConsumption.toFixed(0)} kWh`, "From bill or monthly array sum before applying morning/day percentage.", pageH);
  y = row(doc, y, "Morning consumption percentage", `${morningConsumptionPct.toFixed(1)}%`, "User input on first design page. If not provided, defaults to 100%.", pageH);
  if (customTariffSarPerKwh != null) {
    y = row(doc, y, "Custom tariff override", `${customTariffSarPerKwh.toFixed(4)} SAR/kWh`, "User input on first workflow page. When provided, this overrides SEC/default tariff logic in financial calculations.", pageH);
  }
  y = row(doc, y, "Yearly consumption used in calculations", `${effectiveYearlyConsumption.toFixed(0)} kWh`, "Calculated: original yearly consumption x (morning percentage / 100). This value is used for sizing, offset, and financial calculations.", pageH);
  y = row(doc, y, "Monthly consumption used", `[12 values] sum = ${effectiveYearlyConsumption.toFixed(0)} kWh`, "Monthly profile after applying morning/day percentage factor.", pageH);
  if (data.gridPhase != null) y = row(doc, y, "Grid phase", `${data.gridPhase}`, "User input (Electrical).", pageH);
  if (data.acCableDistanceM != null) y = row(doc, y, "AC cable distance", `${data.acCableDistanceM} m`, "User input (Electrical).", pageH);
  if (data.dcCableDistanceM != null) y = row(doc, y, "DC cable distance", `${data.dcCableDistanceM} m`, "User input (Electrical).", pageH);
  y += 4;

  // ---------- 2. Weather data ----------
  if (y > pageH - 40) { doc.addPage(); y = 20; }
  doc.setFontSize(12);
  doc.setTextColor(234, 88, 12);
  doc.text("2. Weather data", 15, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  y = row(
    doc,
    y,
    "Source",
    "NREL NSRDB MSG API only",
    "NREL NSRDB MSG job flow only: submit coordinate request, poll outputs.downloadUrl until ready, download ZIP, extract CSV, and aggregate monthly ghi/dni/dhi, air_temperature, and wind_speed.",
    pageH
  );
  y = row(
    doc,
    y,
    "GHI/DNI/DHI per month",
    "kWh/mÂ²/day (daily avg)",
    "Aggregated from NSRDB interval W/m2 samples: sum value * dtHours / 1000 to kWh/m2/month, then divide by observed days in each month from CSV day values to get kWh/m2/day.",
    pageH
  );
  y = row(doc, y, "Temp & wind per month", "C, m/s", "Monthly means of NREL air_temperature and wind_speed from the NSRDB MSG CSV download.", pageH);
  y += 4;

  // ---------- 3. System sizing ----------
  if (y > pageH - 50) { doc.addPage(); y = 20; }
  doc.setFontSize(12);
  doc.setTextColor(234, 88, 12);
  doc.text("3. System sizing", 15, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  const modW = data.selectedModule?.power_stc ?? 550;
  const initialSystemSize = data.design.initialSystemSize ?? data.design.systemSize;
  const inverterAcCapKw =
    typeof data.design.inverterAcCapKw === "number" && Number.isFinite(data.design.inverterAcCapKw)
      ? data.design.inverterAcCapKw
      : initialSystemSize / 1.3;
  const selectedInverterAcKw =
    typeof data.design.selectedInverterAcKw === "number" && Number.isFinite(data.design.selectedInverterAcKw)
      ? data.design.selectedInverterAcKw
      : (data.selectedInverter?.power_ac_nominal ?? (res?.inverterSize ?? data.design.systemSize / 1.3));
  const selectedInverterCount =
    typeof data.design.selectedInverterCount === "number" && Number.isFinite(data.design.selectedInverterCount)
      ? data.design.selectedInverterCount
      : 1;
  const dcAcRatioFinal =
    typeof data.design.dcAcRatioFinal === "number" && Number.isFinite(data.design.dcAcRatioFinal)
      ? data.design.dcAcRatioFinal
      : (selectedInverterAcKw > 0 ? data.design.systemSize / selectedInverterAcKw : 0);
  const selectionModeLabel = data.userType === "residential" ? "single" : "multi";
  y = row(doc, y, "Target offset", "100%", "Constant: offsetTarget = 1.0 in calculateSystemSizeFromConsumption.", pageH);
  y = row(doc, y, "Initial system size (kWp)", initialSystemSize.toFixed(2), "Calculated from effective consumption (after morning percentage), weather-based specific production, and target offset.", pageH);
  y = row(doc, y, "Inverter AC cap (kW)", inverterAcCapKw.toFixed(2), "Calculated as initialSystemSize / 1.3.", pageH);
  y = row(doc, y, "Inverter selection mode", selectionModeLabel, "Rule: residential uses single inverter; commercial/industrial/agricultural use multi-inverter packing.", pageH);
  y = row(doc, y, "Selected inverter AC (kW)", selectedInverterAcKw.toFixed(2), "Selected from equipment DB under AC cap (single mode for residential, multi mode for other user types).", pageH);
  y = row(doc, y, "Selected inverter count", `${selectedInverterCount}`, "Single inverter for residential; packed identical units for commercial/industrial/agricultural.", pageH);
  y = row(doc, y, "Final system size (kWp)", data.design.systemSize.toFixed(2), "Calculated from selected inverter AC: DC target = inverter AC x 1.3, with panel count floored to keep DC/AC <= 1.3.", pageH);
  y = row(doc, y, "Module power (W)", `${modW}`, data.selectedModule ? `Selected from equipment DB: ${data.selectedModule.manufacturer} ${data.selectedModule.model}.` : "Default 550 W if no module selected.", pageH);
  y = row(doc, y, "Panel count", `${data.design.panelCount}`, "Calculated: floor((selectedInverterAC x 1.3) x 1000 / modulePower), minimum 1 panel.", pageH);
  y = row(doc, y, "Final DC:AC ratio", dcAcRatioFinal.toFixed(3), "Constrained by rule: final ratio must be <= 1.3.", pageH);
  y = row(doc, y, "Inverter size used in simulation (kW)", res ? res.inverterSize.toFixed(2) : selectedInverterAcKw.toFixed(2), "Solar engine uses inverterSizeOverrideKw so AC clipping follows selected inverter capacity.", pageH);
  if (data.selectedInverter) y = row(doc, y, "Inverter model", `${data.selectedInverter.manufacturer} ${data.selectedInverter.model}`, "Selected from equipment DB according to constrained inverter-cap rules.", pageH);
  y += 4;

  // ---------- 4. Constants (engine & financial) ----------
  if (y > pageH - 60) { doc.addPage(); y = 20; }
  doc.setFontSize(12);
  doc.setTextColor(234, 88, 12);
  doc.text("4. Constants used in calculations", 15, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  y = row(doc, y, "Soiling loss (residential)", "4%", "solarCalculationEngine: getSoilingDefault(userType). Commercial 5%, industrial 6%, agricultural 10%.", pageH);
  y = row(doc, y, "Shading", "none (0% loss)", "Default shadingLevel 'none'. Partial 12.5%, significant 25%.", pageH);
  y = row(doc, y, "Diffuse fraction", "0.3", "Transposition: typical for Saudi (beam/diffuse split).", pageH);
  y = row(doc, y, "Ground reflectance", "0.2", "Transposition: albedo constant.", pageH);
  y = row(doc, y, "Temp coefficient", "-0.4%/Â°C", "Module power loss above 25Â°C (solarCalculationEngine).", pageH);
  y = row(doc, y, "Mismatch loss", "2%", "Constant 0.98 in calculateArrayOutput.", pageH);
  y = row(doc, y, "Wiring loss", "2%", "Constant 0.98 in calculateArrayOutput.", pageH);
  y = row(doc, y, "Module quality loss", "2%", "Constant 0.98 in calculateArrayOutput.", pageH);
  y = row(
    doc,
    y,
    "Tariffs (SEC)",
    customTariffSarPerKwh != null ? "Overridden by custom tariff" : "Tiered / flat",
    customTariffSarPerKwh != null
      ? "financialCalculationService: customTariffSarPerKwh is provided by user and overrides SEC/default tariff calculations."
      : "financialCalculationService: annual tariff is computed as annualBill / annualConsumption using SEC tariff tables (residential 0.18/0.30, commercial 0.22/0.32, agricultural 0.18/0.22, industrial 0.20 flat).",
    pageH
  );
  y = row(doc, y, "25-year savings factor", "25 x 0.9", "Configured formula: savings25Year = (tariff x firstYearProduction) x 25 x 0.9.", pageH);
  y += 4;

  // ---------- 5. Monthly energy (per month: GHI, temp, POA, array, grid, PR) ----------
  if (y > pageH - 30) { doc.addPage(); y = 20; }
  doc.setFontSize(12);
  doc.setTextColor(234, 88, 12);
  doc.text("5. Monthly energy values (sources)", 15, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  y = row(doc, y, "GHI (per month)", "kWh/mÂ²", "From weather data for this location (see section 2).", pageH);
  y = row(doc, y, "Ambient temp (per month)", "Â°C", "From weather data for this location.", pageH);
  y = row(doc, y, "POA (Plane of Array)", "kWh/mÂ²", "Calculated: transposeIrradiation(ghi, lat, tilt, azimuth, month). Simplified Perez-style tilt model + IAM.", pageH);
  y = row(doc, y, "Array output (DC)", "kWh", "Calculated: POA Ã— systemSize Ã— temp factor Ã— mounting Ã— surface Ã— mismatch Ã— wiring Ã— soiling Ã— module quality.", pageH);
  y = row(doc, y, "Grid output (AC)", "kWh", "Calculated: arrayOutput Ã— inverterEfficiency (load-dependent); clipped at inverter capacity.", pageH);
  y = row(doc, y, "Performance ratio (monthly)", "%", "PR = (gridOutput / (POA Ã— systemSize)) Ã— 100 (STC-normalized).", pageH);
  y += 4;

  if (res && res.monthlyResults && res.monthlyResults.length > 0 && y < pageH - 80) {
    doc.setFontSize(10);
    doc.text("Monthly table (values from calculation engine):", 15, y);
    y += 6;
    const tableBody = res.monthlyResults.map(m => [
      MONTH_NAMES[m.month - 1],
      m.ghi.toFixed(1),
      m.temp.toFixed(1),
      m.poa.toFixed(1),
      m.arrayOutput.toFixed(0),
      m.gridOutput.toFixed(0),
      m.performanceRatio.toFixed(1)
    ]);
    autoTable(doc, {
      startY: y,
      head: [["Month", "GHI", "Temp", "POA", "Array", "Grid", "PR%"]],
      body: tableBody,
      theme: "grid",
      styles: { fontSize: 8 },
      margin: { left: 15 },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ---------- 6. Annual results & losses ----------
  if (y > pageH - 70) { doc.addPage(); y = 20; }
  doc.setFontSize(12);
  doc.setTextColor(234, 88, 12);
  doc.text("6. Annual results and losses", 15, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  if (res) {
    y = row(doc, y, "Annual production", `${res.annualProduction.toFixed(0)} kWh`, "Sum of monthly grid output (AC).", pageH);
    y = row(doc, y, "Specific production", `${res.specificProduction.toFixed(0)} kWh/kWp/year`, "annualProduction / actualSystemSize.", pageH);
    y = row(doc, y, "Performance ratio (annual)", `${res.performanceRatio.toFixed(1)}%`, "annualProduction / (totalPOA Ã— systemSize) Ã— 100.", pageH);
    y = row(doc, y, "Capacity factor", `${res.capacityFactor.toFixed(1)}%`, "annualProduction / (systemSize Ã— 8760) Ã— 100.", pageH);
    y = row(doc, y, "Solar irradiation (theoretical)", `${res.energyFlow.solarIrradiation.toFixed(0)} kWh`, "totalPOA Ã— systemSize.", pageH);
    y = row(doc, y, "Array output (DC)", `${res.energyFlow.arrayOutput.toFixed(0)} kWh`, "Sum of monthly DC after losses.", pageH);
    y = row(doc, y, "Array losses", `${res.losses.arrayLosses.toFixed(0)} kWh (${res.losses.arrayLossesPercent.toFixed(1)}%)`, "Temperature, mismatch, wiring, soiling, module quality.", pageH);
    y = row(doc, y, "Inverter losses", `${res.losses.inverterLosses.toFixed(0)} kWh (${res.losses.inverterLossesPercent.toFixed(1)}%)`, "DC to AC conversion and clipping.", pageH);
    y = row(doc, y, "Energy offset", `${data.design.offset}%`, "min(100, round(annualProduction / yearlyConsumption Ã— 100)).", pageH);
  }
  y += 4;

  // ---------- 7. BOQ ----------
  if (y > pageH - 50) { doc.addPage(); y = 20; }
  doc.setFontSize(12);
  doc.setTextColor(234, 88, 12);
  doc.text("7. Bill of quantities (BOQ)", 15, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  if (data.boq) {
    y = row(doc, y, "Subtotal (SAR)", data.boq.subtotal.toLocaleString("en-SA"), "Sum of all line items (boqCalculationService).", pageH);
    y = row(doc, y, "VAT", "15%", "Constant in Saudi (boqCalculationService).", pageH);
    y = row(doc, y, "VAT amount (SAR)", data.boq.vat.toLocaleString("en-SA"), "subtotal Ã— 0.15.", pageH);
    y = row(doc, y, "Grand total (SAR)", data.boq.grandTotal.toLocaleString("en-SA"), "subtotal + vat. Source: BOQ calculation with equipment DB and cable/mounting pricing.", pageH);
  } else {
    y = row(doc, y, "BOQ", "â€”", "Not available for this design.", pageH);
  }
  y += 4;

  // ---------- 8. Financials ----------
  if (y > pageH - 80) { doc.addPage(); y = 20; }
  doc.setFontSize(12);
  doc.setTextColor(234, 88, 12);
  doc.text("8. Financial metrics", 15, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  y = row(doc, y, "Total cost (SAR)", data.financials.totalCost.toLocaleString("en-SA"), "From BOQ grand total (incl. VAT).", pageH);
  y = row(
    doc,
    y,
    "Electricity rate",
    `${derivedTariffSarPerKwh.toFixed(4)} SAR/kWh`,
    customTariffSarPerKwh != null
      ? "financialCalculationService: user-provided customTariffSarPerKwh overrides SEC/default tariff logic."
      : "financialCalculationService: annual electricity bill / annual consumption, based on SEC tariff tables.",
    pageH
  );
  y = row(doc, y, "First year savings (SAR)", data.financials.savingsYear1.toLocaleString("en-SA"), "Formula: tariff x first-year production.", pageH);
  y = row(doc, y, "25-year savings (SAR)", data.financials.savings25Year.toLocaleString("en-SA"), "Formula: (tariff x first-year production) x 25 x 0.9.", pageH);
  y = row(doc, y, "Payback period (years)", data.financials.paybackPeriod.toFixed(1), "Simple payback: totalCost / firstYearSavings.", pageH);
  y = row(doc, y, "ROI (25-year) %", data.financials.roi.toFixed(1), "(savings25Year - totalCost) / totalCost Ã— 100.", pageH);
  if (data.financials.monthlyPayment > 0) y = row(doc, y, "Monthly payment (loan)", data.financials.monthlyPayment.toLocaleString("en-SA") + " SAR", "If financingType = loan: 4.5% interest, 10 yr, 20% down.", pageH);
  y += 4;

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Calculation Sources â€“ Page ${i} of ${pageCount}`, 105, 287, { align: "center" });
  }

  const filename = `${data.projectName.replace(/[^a-z0-9]/gi, "_")}_Calculation_Sources.pdf`;
  const disposition = options?.disposition ?? "download";
  if (disposition === "blob") {
    return doc.output("blob");
  }
  if (disposition === "both") {
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return blob;
  }
  doc.save(filename);
}

